import { CoinsIcon, PercentIcon, TimeIcon } from "components/mortgage/icons";
import useSendHeightToParent from "hooks/useSendHeightToParent";
import indexCss from "index.module.css";
import { cn, formatNumber } from "lib/utils";
import { KeyboardEvent, useCallback, useState } from "react";
import styles from "./page.module.css";

// Default values
const DEFAULT_AMOUNT = "800000";
const DEFAULT_YEARS = "30";
const DEFAULT_RATE = "2.60";

// Strip common number separators (commas, spaces, underscores) and parse as number
const parseNumberString = (value: string): number => {
  // Remove commas, spaces, underscores (common thousand separators)
  const cleaned = value.replace(/[,\s_]/g, "");
  return parseFloat(cleaned);
};

// Parse and validate URL params
const getInitialValues = () => {
  const params = new URLSearchParams(window.location.search);
  
  const amountParam = params.get("amount");
  const yearsParam = params.get("years");
  const rateParam = params.get("rate");
  
  // Validate amount: must be a positive number (strip separators)
  let amount = DEFAULT_AMOUNT;
  if (amountParam) {
    const parsed = parseNumberString(amountParam);
    if (!isNaN(parsed) && parsed > 0 && Number.isFinite(parsed)) {
      amount = String(Math.floor(parsed)); // Store as integer string
    }
  }
  
  // Validate years: must be a positive integer (strip separators)
  let years = DEFAULT_YEARS;
  if (yearsParam) {
    const parsed = parseNumberString(yearsParam);
    if (!isNaN(parsed) && parsed > 0 && Number.isFinite(parsed)) {
      years = String(Math.floor(parsed));
    }
  }
  
  // Validate rate: must be a positive number (strip separators)
  let rate = DEFAULT_RATE;
  if (rateParam) {
    const parsed = parseNumberString(rateParam);
    if (!isNaN(parsed) && parsed > 0 && Number.isFinite(parsed)) {
      rate = parsed.toFixed(2);
    }
  }
  
  return { amount, years, rate };
};

// Update URL with current values
const updateURL = (amount: string, years: string, rate: string) => {
  const params = new URLSearchParams();
  // Strip any separators before putting in URL
  params.set("amount", amount.replace(/[,\s_]/g, "") || "0");
  params.set("years", years.replace(/[,\s_]/g, "") || "0");
  params.set("rate", rate.replace(/[,\s_]/g, "") || "0");
  
  const newUrl = `${window.location.pathname}?${params.toString()}`;
  window.history.replaceState(null, "", newUrl);
};

// Up/Down arrow buttons component
const StepperButtons = ({
  onUp,
  onDown,
  blinkUp,
  blinkDown,
}: {
  onUp: () => void;
  onDown: () => void;
  blinkUp?: boolean;
  blinkDown?: boolean;
}) => (
  <span className={styles.stepperButtons}>
    <button type="button" className={cn(styles.stepperBtn, blinkUp && styles.stepperBtnBlink)} onClick={onUp} tabIndex={-1} aria-label="Increase">
      <svg viewBox="0 0 10 6" fill="currentColor"><path d="M5 0L10 6H0L5 0Z" /></svg>
    </button>
    <button type="button" className={cn(styles.stepperBtn, blinkDown && styles.stepperBtnBlink)} onClick={onDown} tabIndex={-1} aria-label="Decrease">
      <svg viewBox="0 0 10 6" fill="currentColor"><path d="M5 6L0 0H10L5 6Z" /></svg>
    </button>
  </span>
);

// Format currency with proper thousand separators
const formatCurrencyDisplay = (value: number, decimals: number = 0): string => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

// Currency display component with split dollars and cents
const CurrencyDisplay = ({ value, className }: { value: number; className?: string }) => {
  // Round to 2 decimal places first to avoid cents overflow (e.g., 0.996 → 100 cents)
  const rounded = Math.round(value * 100) / 100;
  const dollars = Math.floor(rounded);
  const cents = String(Math.round((rounded % 1) * 100)).padStart(2, "0");
  return (
    <span className={className} data-value={value}>
      ${formatCurrencyDisplay(dollars, 0)}
      <span className={styles.currencyDisplayCents}>.{cents}</span>
    </span>
  );
};

// Replace all digits with 0 for consistent input sizer width
const replaceDigitsWithZero = (value: string): string => {
  if (!value) return "0";
  return value.replace(/\d/g, "0");
};

export default function MortgagePage() {
  useSendHeightToParent();

  // Get initial values from URL params (or defaults)
  const [initialValues] = useState(getInitialValues);
  const [loanAmount, setLoanAmount] = useState<string>(initialValues.amount);
  const [loanTenure, setLoanTenure] = useState<string>(initialValues.years);
  const [interestRate, setInterestRate] = useState<string>(initialValues.rate);

  // Blink states for stepper buttons
  const [blinkLoanUp, setBlinkLoanUp] = useState(false);
  const [blinkLoanDown, setBlinkLoanDown] = useState(false);
  const [blinkTenureUp, setBlinkTenureUp] = useState(false);
  const [blinkTenureDown, setBlinkTenureDown] = useState(false);
  const [blinkRateUp, setBlinkRateUp] = useState(false);
  const [blinkRateDown, setBlinkRateDown] = useState(false);

  const triggerBlink = (setter: (v: boolean) => void) => {
    setter(true);
    setTimeout(() => setter(false), 150);
  };

  // Parse values for calculation
  const loanAmountNum = parseFloat(loanAmount.replace(/,/g, "")) || 0;
  const loanTenureNum = parseFloat(loanTenure) || 0;
  const interestRateNum = parseFloat(interestRate) || 0;

  // Calculate mortgage
  const calculateMortgage = useCallback(() => {
    const principal = loanAmountNum;
    const monthlyRate = interestRateNum / 100 / 12;
    const numberOfPayments = loanTenureNum * 12;

    if (principal <= 0 || loanTenureNum <= 0 || interestRateNum <= 0) {
      return {
        monthlyPayment: 0,
        totalAmount: 0,
        totalInterest: 0,
        principalPercentage: 0,
        interestPercentage: 0,
      };
    }

    // Monthly payment formula: M = P * [r(1+r)^n] / [(1+r)^n - 1]
    const monthlyPayment =
      monthlyRate === 0
        ? principal / numberOfPayments
        : (principal * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments))) /
          (Math.pow(1 + monthlyRate, numberOfPayments) - 1);

    const totalAmount = monthlyPayment * numberOfPayments;
    const totalInterest = totalAmount - principal;

    const principalPercentage = (principal / totalAmount) * 100;
    const interestPercentage = (totalInterest / totalAmount) * 100;

    return {
      monthlyPayment,
      totalAmount,
      totalInterest,
      principalPercentage,
      interestPercentage,
    };
  }, [loanAmountNum, loanTenureNum, interestRateNum]);

  const results = calculateMortgage();

  const handleLoanAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setLoanAmount(value);
    updateURL(value, loanTenure, interestRate);
  };

  const handleLoanTenureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setLoanTenure(value);
    updateURL(loanAmount, value, interestRate);
  };

  const handleInterestRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9.]/g, "");
    // Only allow one decimal point
    const parts = value.split(".");
    if (parts.length > 2) return;
    setInterestRate(value);
    updateURL(loanAmount, loanTenure, value);
  };

  // Step adjustments for keyboard and buttons
  const stepLoanAmount = (delta: number) => {
    const newVal = Math.max(0, loanAmountNum + delta);
    const newValStr = String(newVal);
    setLoanAmount(newValStr);
    updateURL(newValStr, loanTenure, interestRate);
  };

  const stepLoanTenure = (delta: number) => {
    const newVal = Math.max(0, loanTenureNum + delta);
    const newValStr = String(newVal);
    setLoanTenure(newValStr);
    updateURL(loanAmount, newValStr, interestRate);
  };

  const stepInterestRate = (delta: number) => {
    const newVal = Math.max(0, interestRateNum + delta);
    const newValStr = newVal.toFixed(2);
    setInterestRate(newValStr);
    updateURL(loanAmount, loanTenure, newValStr);
  };

  // Keyboard handlers
  const handleLoanAmountKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowUp") { e.preventDefault(); stepLoanAmount(1000); triggerBlink(setBlinkLoanUp); }
    if (e.key === "ArrowDown") { e.preventDefault(); stepLoanAmount(-1000); triggerBlink(setBlinkLoanDown); }
  };

  const handleLoanTenureKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowUp") { e.preventDefault(); stepLoanTenure(1); triggerBlink(setBlinkTenureUp); }
    if (e.key === "ArrowDown") { e.preventDefault(); stepLoanTenure(-1); triggerBlink(setBlinkTenureDown); }
  };

  const handleInterestRateKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowUp") { e.preventDefault(); stepInterestRate(0.1); triggerBlink(setBlinkRateUp); }
    if (e.key === "ArrowDown") { e.preventDefault(); stepInterestRate(-0.1); triggerBlink(setBlinkRateDown); }
  };

  return (
    <div className={cn(indexCss.body, styles.pageWrapper)}>
      <div className={styles.container}>
        <h1 className={styles.title}>
          <span className={styles.titlePhrase}>Plan Your Mortgage,</span>{' '}
          <span className={styles.titlePhrase}>Protect Your Home</span>
        </h1>

        {/* Input Cards */}
        <div className={styles.inputCards}>
          {/* Loan Amount Card */}
          <div className={styles.inputCard}>
            <div className={styles.cardAccent} />
            <div className={styles.cardContent}>
              <div className={styles.cardIcon}>
                <CoinsIcon />
              </div>
              <div className={styles.cardInputArea}>
                <label htmlFor="loanAmount" className={styles.cardLabel}>Loan Amount</label>
                <div className={styles.cardValue}>
                  <span className={styles.currencyPrefix}>$</span>
                  <span className={styles.autoSizeInputWrapper}>
                    <span className={styles.inputSizer}>{replaceDigitsWithZero(formatNumber(loanAmount))}</span>
                    <input
                      id="loanAmount"
                      type="text"
                      className={styles.cardInput}
                      value={formatNumber(loanAmount)}
                      onChange={handleLoanAmountChange}
                      onKeyDown={handleLoanAmountKeyDown}
                    />
                  </span>
                  <StepperButtons onUp={() => stepLoanAmount(1000)} onDown={() => stepLoanAmount(-1000)} blinkUp={blinkLoanUp} blinkDown={blinkLoanDown} />
                </div>
              </div>
            </div>
          </div>

          {/* Loan Tenure Card */}
          <div className={styles.inputCard}>
            <div className={styles.cardAccent} />
            <div className={styles.cardContent}>
              <div className={styles.cardIcon}>
                <TimeIcon />
              </div>
              <div className={styles.cardInputArea}>
                <label htmlFor="loanTenure" className={styles.cardLabel}>Loan Tenure</label>
                <div className={styles.cardValue}>
                  <span className={styles.autoSizeInputWrapper}>
                    <span className={styles.inputSizer}>{replaceDigitsWithZero(loanTenure)}</span>
                    <input
                      id="loanTenure"
                      type="text"
                      className={styles.cardInputSmall}
                      value={loanTenure}
                      onChange={handleLoanTenureChange}
                      onKeyDown={handleLoanTenureKeyDown}
                    />
                  </span>
                  <StepperButtons onUp={() => stepLoanTenure(1)} onDown={() => stepLoanTenure(-1)} blinkUp={blinkTenureUp} blinkDown={blinkTenureDown} />
                  <span className={styles.unitSuffix}>Year(s)</span>
                </div>
              </div>
            </div>
          </div>

          {/* Interest Rate Card */}
          <div className={styles.inputCard}>
            <div className={styles.cardAccent} />
            <div className={styles.cardContent}>
              <div className={styles.cardIcon}>
                <PercentIcon />
              </div>
              <div className={styles.cardInputArea}>
                <label htmlFor="interestRate" className={styles.cardLabel}>Annual Interest Rate</label>
                <div className={styles.cardValue}>
                  <span className={styles.autoSizeInputWrapper}>
                    <span className={styles.inputSizer}>{replaceDigitsWithZero(interestRate)}</span>
                    <input
                      id="interestRate"
                      type="text"
                      className={styles.cardInputSmall}
                      value={interestRate}
                      onChange={handleInterestRateChange}
                      onKeyDown={handleInterestRateKeyDown}
                    />
                  </span>
                  <StepperButtons onUp={() => stepInterestRate(0.1)} onDown={() => stepInterestRate(-0.1)} blinkUp={blinkRateUp} blinkDown={blinkRateDown} />
                  <span className={styles.unitSuffix}>%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* HDB Note */}
        <p className={styles.hdbNote}>
          HDB interest rate: The Housing &amp; Development Board (HDB) states that the
          concessionary HDB housing loan interest rate is 2.6% per annum, calculated as
          0.1% above the prevailing CPF Ordinary Account (OA) rate.
        </p>

        {/* Results Section */}
        {results.totalAmount > 0 && (
          <div className={styles.resultsContainer}>
            {/* Monthly Payment Header */}
            <div className={styles.monthlyPaymentHeader}>
              <span className={styles.monthlyPaymentLabel}>Monthly Repayment (Est.)</span>
              <CurrencyDisplay className={styles.monthlyPaymentValue} value={results.monthlyPayment} />
            </div>

            {/* Results Content */}
            <div className={styles.resultsContent}>
              <span className={styles.totalCostLabel}>Total Cost of Mortgage (Est.):</span>
              <CurrencyDisplay className={styles.totalCostValue} value={results.totalAmount} />
              <p className={styles.suggestionText}>
                Suggested coverage in the event of critical illness or loss of ability to work.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
